import { isSameDay } from 'date-fns';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { MaxLanguagesCount, MaxProfessionsCount } from '@constants';

const MIN_REQUIRED_COUNT = 1;
const MAX_REQUIRED_COUNT = 50;
const MIN_REQUIRED_LENGTH_COUNT = 1;
const MAX_REQUIRED_LENGTH_COUNT = 2;
const MAX_NAME_LENGTH = 50;
const MAX_COMMENT_LENGTH = 10000;
const MAX_EMPLOYEES = 10;
const MAX_DOMAINS = 10;
const MIN_SALARY = 0;
const MAX_SALARY = 150000;

const SALARY_REGEXP = /^\d+$/;

const isSalaryValue = (value: unknown): boolean => SALARY_REGEXP.test(String(value ?? '').trim());

// The backend checks the deadline against today only when it is set or changed, so a passed deadline can stay as it is.
// An edited request needs at least as many workers as candidates are already found (US-10.3 AC-4)
export const useValidationSchemaRequest = (savedDeadline?: Date | null, minRequiredCount = MIN_REQUIRED_COUNT) => {
  const { t } = useTranslation();
  const validationSchema = useMemo(() => {
    const salaryRangeMessage = t('errors.betweenNumbers', { min: MIN_SALARY, max: MAX_SALARY });
    const salarySchema = Yup.string()
      .trim()
      .transform((value) => value || '')
      .test('salary-range', salaryRangeMessage, (value) => {
        if (!value) return true;
        return isSalaryValue(value) && Number(value) <= MAX_SALARY;
      });

    return Yup.object().shape(
      {
        name: Yup.string()
          .trim()
          .required(t('errors.requiredField'))
          .max(MAX_NAME_LENGTH, t('errors.maxCharacters', { count: MAX_NAME_LENGTH })),
        forPreoffer: Yup.boolean(),
        forProject: Yup.boolean(),
        unitId: Yup.string().required(t('errors.requiredField')),
        divisionId: Yup.string().required(t('errors.requiredField')),
        isBillable: Yup.string().required(t('errors.requiredField')),
        currency: Yup.string().required(t('errors.requiredField')),
        salaryMin: salarySchema.test('salary-min', t('errors.salaryDifferenceMin'), function (salaryMin: string) {
          const salaryMax = this.parent.salaryMax;
          if (!isSalaryValue(salaryMin) || !isSalaryValue(salaryMax)) return true;
          return Number(salaryMin) <= Number(salaryMax);
        }),
        salaryMax: salarySchema.test('salary-max', t('errors.salaryDifferenceMax'), function (salaryMax: string) {
          const salaryMin = this.parent.salaryMin;
          if (!isSalaryValue(salaryMin) || !isSalaryValue(salaryMax)) return true;
          return Number(salaryMax) >= Number(salaryMin);
        }),
        customers: Yup.array()
          .of(
            Yup.object({
              title: Yup.string().required(),
              value: Yup.string().required(),
            }),
          )
          .max(MAX_EMPLOYEES, t('errors.maxAuthorsCount', { count: MAX_EMPLOYEES }))
          .min(1, t('errors.requiredField'))
          .required(t('errors.requiredField')),
        professions: Yup.array()
          .min(1, t('errors.requiredField'))
          .max(MaxProfessionsCount, t('errors.specialtyLimit', { count: MaxProfessionsCount })),
        domains: Yup.array().max(MAX_DOMAINS, t('errors.domainLimit', { count: MAX_DOMAINS })),
        priority: Yup.string().required(t('errors.requiredField')),
        requiredCount: Yup.string()
          .trim()
          .required(t('errors.requiredField'))
          .min(
            MIN_REQUIRED_LENGTH_COUNT,
            t('errors.betweenNumbers', { min: MIN_REQUIRED_COUNT, max: MAX_REQUIRED_COUNT }),
          )
          .max(
            MAX_REQUIRED_LENGTH_COUNT,
            t('errors.betweenNumbers', { min: MIN_REQUIRED_COUNT, max: MAX_REQUIRED_COUNT }),
          )
          .when('requiredCount', {
            is: (requiredCount: string) => requiredCount?.length === 1,
            then: (schema) =>
              schema.matches(
                /^[1-9]$/,
                t('errors.betweenNumbers', { min: MIN_REQUIRED_COUNT, max: MAX_REQUIRED_COUNT }),
              ),
            otherwise: (schema) =>
              schema.matches(
                /(^50$)|(^[1-4]\d$)/,
                t('errors.betweenNumbers', { min: MIN_REQUIRED_COUNT, max: MAX_REQUIRED_COUNT }),
              ),
          })
          .test(
            'found-count',
            t('errors.betweenNumbers', { min: minRequiredCount, max: MAX_REQUIRED_COUNT }),
            (value) => !value || Number(value) >= minRequiredCount,
          ),
        cities: Yup.array(),
        countries: Yup.array(),
        offices: Yup.array(),
        candidateCountry: Yup.string(),
        candidateCity: Yup.string(),
        languages: Yup.array()
          .of(
            Yup.object()
              .shape({
                language: Yup.string().when('level', {
                  is: (level: string) => !!level,
                  then: (schema) => schema.required(t('errors.noLanguage')),
                }),
                level: Yup.string(),
              })
              .test('test', t('errors.languageAlreadyExist'), function (item) {
                const index = parseInt(this.path.replace(/[^\d]/g, ''));
                const duplicates: number[] = [];
                this.parent.forEach((curr, idx) => {
                  if (curr.language === item.language && item.language) {
                    duplicates.push(idx);
                  }
                });
                return duplicates.length <= 1 || duplicates.findIndex((curr) => curr === index) === 0;
              }),
          )
          .max(MaxLanguagesCount, t('errors.maxLanguagesCount', { count: MaxLanguagesCount })),
        comment: Yup.string()
          .trim()
          .max(MAX_COMMENT_LENGTH, t('errors.maxCharacters', { count: MAX_COMMENT_LENGTH })),
        workloads: Yup.array(),
        workFormats: Yup.array(),
        employmentForms: Yup.array(),
        deadline: Yup.date()
          .nullable()
          .test('deadline', t('errors.earlierDate'), (deadline) => {
            if (!deadline || (savedDeadline && isSameDay(deadline, savedDeadline))) return true;
            return deadline >= new Date(new Date().setHours(0, 0, 0, 0));
          }),
      },
      [['requiredCount', 'requiredCount']],
    );
  }, [t, savedDeadline, minRequiredCount]);

  return { validationSchema };
};
